import { createApi } from "@reduxjs/toolkit/query/react";
import { UnusualTextPhrases } from "../../models/responses/suggestion";
import { axiosBaseQuery } from "../axiosConfig";
import { RequestMethod } from "../enum";
import { similarSuggestionRequestBuilder, suggestionRequestBuilder } from "../suggestion/helpers";
import { SearchSuggestionRequest, SimilarSuggestionSearchRequest } from "../suggestion/types";
import { dispatchUpdateTrigrams } from "../worker";
import { FetchTrigramsForSuggestionsArgs, GetUnusualPhrasesRequest } from "./types";

const getClauseTextUnusualPhrases = ({ workspaceId, ...data }: GetUnusualPhrasesRequest) => {
  return {
    url: "",
    method: RequestMethod.POST,
    data,
    workspaceId,
  };
};

export const trigramsApi = createApi({
  reducerPath: "trigramsApi",
  baseQuery: axiosBaseQuery<any>({
    baseUrl: "/clauses/trigrams/search",
  }),
  tagTypes: ["trigrams"],
  endpoints: (build) => ({
    getTrigramsForApp: build.query<Array<UnusualTextPhrases>, string>({
      queryFn: async (_args) => {
        return { data: [] };
      },
      keepUnusedDataFor: 3600,
    }),

    fetchTrigramsForSuggestions: build.query<void, FetchTrigramsForSuggestionsArgs>({
      queryFn: async (args, spi, extraOptions, baseQuery) => {
        const { filter } = suggestionRequestBuilder(args.query as SearchSuggestionRequest, true);
        filter.limit = 100;
        const { data: unusualPhrases } = await baseQuery(
          getClauseTextUnusualPhrases({ filter, workspaceId: args.query.workspaceId })
        );
        if (!unusualPhrases) return { error: null };
        dispatchUpdateTrigrams({
          appId: args.appId,
          clauses: args.clauses.map((s) => ({ id: s.clause_id, text: s.clause_text })),
          unusualPhrases,
        });
        return { error: null };
      },
    }),
    fetchTrigramsForSimilarClauses: build.query<void, FetchTrigramsForSuggestionsArgs>({
      queryFn: async (args, spi, extraOptions, baseQuery) => {
        const { filter } = similarSuggestionRequestBuilder(args.query as SimilarSuggestionSearchRequest);
        filter.limit = 100;
        const { data: unusualPhrases } = await baseQuery(
          getClauseTextUnusualPhrases({ filter, workspaceId: args.query.workspaceId })
        );
        if (!unusualPhrases) return { error: null };
        dispatchUpdateTrigrams({
          appId: args.appId,
          clauses: args.clauses.map((s) => ({ id: s.clause_id, text: s.clause_text })),
          unusualPhrases,
        });
        return { error: null };
      },
    }),

    updateTrigramsForApp: build.mutation<
      Array<UnusualTextPhrases>,
      { appId: string; unusualPhrases: Array<UnusualTextPhrases> }
    >({
      queryFn: async (args) => {
        return { data: args.unusualPhrases };
      },
      onQueryStarted: async (args, { queryFulfilled, dispatch }) => {
        const { data } = await queryFulfilled;
        dispatch(
          trigramsApi.util.updateQueryData("getTrigramsForApp", args.appId, (savedData) => {
            savedData.push(...data);
          })
        );
      },
    }),
  }),
});
